package ai.wagemix.analytics

import ai.wagemix.env
import ai.wagemix.logic.WMDataGateway
import ai.wagemix.logic.WMRoutes
import ai.wagemix.logic.analytics.WMAnalyticsContext
import ai.wagemix.logic.analytics.WMAnalyticsGate
import ai.wagemix.logic.analytics.WMScreenViewStep
import ai.wagemix.logic.analytics.WMUtm
import ai.wagemix.logic.analytics.analyticsProfileFlow
import ai.wagemix.logic.screenOf
import ai.wagemix.net.models.firebase.WMSubscriptionType
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import co.touchlab.kermit.Severity
import com.fsryan.analytics.log.openpanel.FSOpenPanelLogWriter
import com.fsryan.analytics.log.openpanel.FSOpenPanelLogWriterConfig
import com.fsryan.analytics.net.track.models.FSAnalyticsValue
import com.fsryan.foundation.ui.tokens.FSBreakpointTokens
import com.fsryan.foundation.util.log.FSLog
import com.fsryan.foundation.util.log.preLoggingSetup
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.Router
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import org.w3c.dom.url.URLSearchParams
import kotlin.time.Clock

/**
 * The app-level state the analytics gate adds to every event (planning/analytics-plan.md §4), kept current by the
 * site: the route sets the screen, the login session tracker the user and session, the profile the plan, and the
 * app's composition the colour mode. The viewport is read from the window when an event is logged; it is not layout.
 */
internal interface WMSiteAnalyticsContext: WMAnalyticsContext {

    /** The signed-in user's uid, OpenPanel's profile id, or null when nobody is signed in. */
    val uid: String?

    fun signedInAs(uid: String?, loginSessionId: String?)
    fun planIs(plan: WMSubscriptionType?)
    fun screenIs(screen: String)
    fun colorModeIs(colorMode: String)
}

internal fun WMSiteAnalyticsContext(): WMSiteAnalyticsContext = WMSiteAnalyticsContextImpl()

private class WMSiteAnalyticsContextImpl: WMSiteAnalyticsContext {

    override var uid: String? = null
        private set
    override var loginSessionId: String? = null
        private set
    override var screen: String? = null
        private set
    override var plan: WMSubscriptionType? = null
        private set
    override var colorMode: String = "light"
        private set

    override val signedIn: Boolean get() = uid != null

    override val viewport: String
        get() {
            val width = window.innerWidth.toDouble()
            val breakpoints = FSBreakpointTokens.defaultValue
            return when {
                width >= breakpoints.extraLarge -> "extra_large"
                width >= breakpoints.large -> "large"
                width >= breakpoints.expanded -> "expanded"
                width >= breakpoints.medium -> "medium"
                else -> "compact"
            }
        }

    override fun signedInAs(uid: String?, loginSessionId: String?) {
        this.uid = uid
        this.loginSessionId = loginSessionId
    }

    override fun planIs(plan: WMSubscriptionType?) {
        this.plan = plan
    }

    override fun screenIs(screen: String) {
        this.screen = screen
    }

    override fun colorModeIs(colorMode: String) {
        this.colorMode = colorMode
    }
}

/**
 * The site's analytics for this page load (planning/analytics-plan.md §2): installs FSLog's writers, follows the
 * route for screen views, and starts the login session tracker and the profile sync once the object graph exists.
 *
 * Analytics is on when the build's `ANALYTICS_CLIENT_ID` global is set (every build but a prod debug build defaults it:
 * dev and staging to `wagemix-staging`, a prod release to `wagemix`; see `site/build.gradle.kts`). When it is off, nothing is sent, and events still reach the console writer.
 */
internal object WMSiteAnalytics {

    // Process state: this page load's analytics context, browser store and writer.
    private val context = WMSiteAnalyticsContext()
    private val store = WMAnalyticsBrowserStore()
    private val routeState = mutableStateOf<String?>(null)
    private val scope = CoroutineScope(SupervisorJob())
    private var writer: FSOpenPanelLogWriter? = null
    private var config: FSOpenPanelLogWriterConfig? = null
    private var tracker: WMAnalyticsLoginSessionTracker? = null
    private var profileSync: WMAnalyticsProfileSync? = null

    /** The path and query of the current route; it changes when the path does, not when only the query does. */
    val route: State<String?> get() = routeState

    /**
     * Installs FSLog's writers and follows the route. Call once, from `@InitKobweb`, before anything logs: FSLog
     * cannot be configured after its first use.
     */
    fun install(router: Router) {
        store.loginSession()?.let { context.signedInAs(uid = it.uid, loginSessionId = it.id) }
        val clientId = AppGlobals["ANALYTICS_CLIENT_ID"].orEmpty()
        val openPanelWriter = clientId.takeIf { it.isNotBlank() }?.let { id -> FSOpenPanelLogWriter(writerConfig(id)) }
        writer = openPanelWriter
        // The platform (console) writer is always added by FSLog; Verbose keeps its output as it was.
        preLoggingSetup(
            minSeverity = Severity.Verbose,
            logWriters = listOfNotNull(openPanelWriter?.let { WMAnalyticsGate(delegate = it, context = context) }),
        )
        router.addRouteInterceptor { routeChanged(pathQueryAndFragment) }
        // Back and forward do not pass through route interceptors.
        window.addEventListener("popstate", { routeChanged(window.location.pathname + window.location.search) })
        window.addEventListener("pagehide", { flush() })
    }

    /** Starts the login session tracker and, when analytics is on, the profile sync. Call once, after Koin starts. */
    fun startObserving(dataGateway: WMDataGateway) {
        if (tracker != null) {
            return
        }
        tracker = WMAnalyticsLoginSessionTracker(dataGateway = dataGateway, context = context, store = store)
        profileSync = config?.let { configured ->
            WMAnalyticsProfileSync(dataGateway = dataGateway, clientApi = configured.clientApi, store = store)
        }
        dataGateway.analyticsProfileFlow().onEach { profile -> context.planIs(profile?.plan) }.launchIn(scope)
    }

    /** Sends the screen view of [route]; the app calls it each time [route] changes. */
    fun screenViewed(route: String) {
        val step = WMScreenViewStep.of(
            stored = store.visit(),
            path = route,
            landingUtm = landingUtm(),
            referrer = document.referrer,
            siteOrigin = window.location.origin,
            now = Clock.System.now(),
        )
        store.storeVisit(step.visit)
        FSLog.sendWMScreenView(screenView = step.screenView, title = document.title, origin = window.location.origin)
    }

    /** Records the colour mode in effect, `light` or `dark`. */
    fun colorModeIs(dark: Boolean) = context.colorModeIs(if (dark) "dark" else "light")

    private fun routeChanged(pathQueryAndFragment: String) {
        val pathAndQuery = pathQueryAndFragment.substringBefore('#')
        val path = pathAndQuery.substringBefore('?')
        if (path != routeState.value?.substringBefore('?')) {
            context.screenIs(WMRoutes.screenOf(path))
            routeState.value = pathAndQuery
        }
    }

    private fun flush() {
        val toFlush = writer ?: return
        scope.launch { toFlush.flush() }
    }

    private fun writerConfig(clientId: String): FSOpenPanelLogWriterConfig {
        val appVersion = AppGlobals["APP_VERSION"].orEmpty().ifEmpty { "unknown" }
        return FSOpenPanelLogWriterConfig(
            baseUrl = "https://analytics-api.fsryan.com",
            clientId = clientId,
            clientSecret = null,
            appName = "wagemix-site",
            appVersion = appVersion,
            appVersionCode = 1,
            staticProperties = mapOf(
                "site_env" to FSAnalyticsValue.Text(AppGlobals.env()),
                "app_version" to FSAnalyticsValue.Text(appVersion),
                "platform" to FSAnalyticsValue.Text("web"),
            ),
            profileIdProvider = { context.uid },
        ).also { config = it }
    }

    private fun landingUtm(): WMUtm {
        val params = URLSearchParams(window.location.search)
        return WMUtm(
            source = params.get("utm_source"),
            medium = params.get("utm_medium"),
            campaign = params.get("utm_campaign"),
            content = params.get("utm_content"),
            term = params.get("utm_term"),
        )
    }
}
